import React, { useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, HeartHandshake, Layers, Users, Wrench, Siren, Handshake, Check, X, Award, FileDown, Clapperboard, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { api, API, getClientId } from "../lib/api";
import { courseLessons, courseQuiz, awarenessReels } from "../data/course";

const icons = { HeartHandshake, Layers, Users, Wrench, Siren, Handshake };

const L = {
  de: { kicker: "Kurs · Marken-Klasse 41", title: "Para-aware Reiten & Helfen", sub: "Ein kompaktes Modul, das Reiter:innen für Para-Reitsport sensibilisiert – mit kurzem Wissens-Check am Ende.", lessons: "Lektionen", quiz: "Wissens-Check", start: "Quiz starten", submit: "Auswerten", retry: "Nochmal", result: "Ergebnis", passed: "Bestanden – du bist Para-aware!", failed: "Fast! Schau nochmal in die Lektionen und versuch es erneut.", of: "von", reels: "Awareness-Reel-Serie", reelsSub: "Fertige Kurz-Skripte & Storyboards für Instagram/TikTok.", hook: "Hook", script: "Skript", visual: "Visual", downloads: "Downloads (PDF)", arena: "Hallen-Knigge", helper: "Helfer-Leitfaden", certTitle: "Dein Zertifikat", certPlaceholder: "Dein Name für das Zertifikat", certBtn: "Zertifikat herunterladen" },
  en: { kicker: "Course · Brand class 41", title: "Para-aware Riding & Helping", sub: "A compact module that sensitises riders to para-equestrian sport – with a short knowledge check at the end.", lessons: "Lessons", quiz: "Knowledge check", start: "Start quiz", submit: "Evaluate", retry: "Retry", result: "Result", passed: "Passed – you're para-aware!", failed: "Almost! Revisit the lessons and try again.", of: "of", reels: "Awareness reel series", reelsSub: "Ready-made short scripts & storyboards for Instagram/TikTok.", hook: "Hook", script: "Script", visual: "Visual", downloads: "Downloads (PDF)", arena: "Arena etiquette", helper: "Helper guide", certTitle: "Your certificate", certPlaceholder: "Your name for the certificate", certBtn: "Download certificate" },
};

export default function Course() {
  const { lang } = useLang();
  const t = L[lang];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [certName, setCertName] = useState("");

  const score = courseQuiz.filter((q) => answers[q.id] === q.correct).length;
  const passed = score / courseQuiz.length >= 0.7;

  const submit = async () => {
    setSubmitted(true);
    try {
      await api.post("/course/complete", { client_id: getClientId(), course_id: "para-aware", score, total: courseQuiz.length });
    } catch (err) { console.error("course complete failed", err); }
    if (score / courseQuiz.length >= 0.7) toast.success(t.passed);
  };
  const reset = () => { setAnswers({}); setSubmitted(false); };

  const sharepic = () => {
    const c = document.createElement("canvas");
    c.width = 1080; c.height = 1080;
    const x = c.getContext("2d");
    x.fillStyle = "#061520"; x.fillRect(0, 0, 1080, 1080);
    x.strokeStyle = "#8C6B34"; x.lineWidth = 6; x.strokeRect(40, 40, 1000, 1000);
    x.textAlign = "center";
    x.fillStyle = "#E0A526"; x.font = "600 30px 'IBM Plex Sans', sans-serif";
    x.fillText("CIRCLE OF IKARUS", 540, 150);
    x.fillStyle = "#EFE7D4"; x.font = "700 92px 'Cormorant Garamond', serif";
    x.fillText("Ich bin", 540, 470);
    x.fillStyle = "#E0A526"; x.font = "700 130px 'Cormorant Garamond', serif";
    x.fillText("Para-aware", 540, 610);
    if (certName.trim()) {
      x.fillStyle = "#EFE7D4"; x.font = "400 46px 'Cormorant Garamond', serif";
      x.fillText(certName.trim(), 540, 720);
    }
    x.fillStyle = "#9FA9AC"; x.font = "400 26px 'IBM Plex Sans', sans-serif";
    x.fillText(lang === "de" ? "Sicherheit ist kein Zufall. Sie ist ein System." : "Safety is not luck. It is a system.", 540, 900);
    x.fillStyle = "#8C6B34"; x.font = "500 24px 'IBM Plex Sans', sans-serif";
    x.fillText("circleofikarus.com", 540, 980);
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png"); a.download = "para-aware-sharepic.png"; a.click();
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:py-16">
      <div className="flex items-center gap-2 instrument-label text-sonne"><GraduationCap size={16} /> {t.kicker}</div>
      <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">{t.title}</h1>
      <p className="mt-4 text-wachs-dim max-w-2xl leading-relaxed">{t.sub}</p>

      {/* Lessons */}
      <h2 className="mt-10 font-serif text-2xl text-sonne">{t.lessons}</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {courseLessons.map((les, i) => {
          const Icon = icons[les.icon] || HeartHandshake;
          const c = lang === "de" ? les.de : les.en;
          return (
            <motion.div key={les.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              data-testid={`lesson-${les.id}`} className="grid-panel rounded-lg p-5">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-md bg-sonne/12 border border-sonne/30 flex items-center justify-center text-sonne shrink-0"><Icon size={18} /></span>
                <span className="font-mono text-xs text-wachs-dim">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-xl">{c.t}</h3>
              </div>
              <p className="mt-3 text-sm text-wachs-dim leading-relaxed">{c.d}</p>
            </motion.div>
          );
        })}
      </div>

      {/* PDF downloads */}
      <h2 className="mt-12 font-serif text-2xl text-sonne">{t.downloads}</h2>
      <div className="mt-4 flex flex-wrap gap-3">
        <a href={`${API}/guides/arena-etiquette.pdf?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="download-arena"
          className="inline-flex items-center gap-2 border border-bronze/40 text-bronze-soft px-5 py-3 rounded-md hover:bg-bronze/10 transition-colors">
          <FileDown size={16} /> {t.arena}
        </a>
        <a href={`${API}/guides/helper-guide.pdf?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="download-helper"
          className="inline-flex items-center gap-2 border border-bronze/40 text-bronze-soft px-5 py-3 rounded-md hover:bg-bronze/10 transition-colors">
          <FileDown size={16} /> {t.helper}
        </a>
      </div>

      {/* Quiz */}
      <h2 className="mt-12 font-serif text-2xl text-sonne">{t.quiz}</h2>
      <div className="mt-4 space-y-4">
        {courseQuiz.map((q, qi) => (
          <div key={q.id} data-testid={`quiz-${q.id}`} className="grid-panel rounded-lg p-5">
            <p className="font-medium">{qi + 1}. {lang === "de" ? q.de : q.en}</p>
            <div className="mt-3 space-y-2">
              {q.options.map((opt, oi) => {
                const chosen = answers[q.id] === oi;
                const isCorrect = q.correct === oi;
                let cls = "border-wachs-dim/25 hover:border-sonne/50 text-wachs-dim";
                if (submitted && isCorrect) cls = "border-ready bg-ready/10 text-ready";
                else if (submitted && chosen && !isCorrect) cls = "border-danger bg-danger/10 text-danger";
                else if (chosen) cls = "border-sonne bg-sonne/10 text-sonne";
                return (
                  <button key={oi} disabled={submitted} onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                    data-testid={`quiz-${q.id}-opt-${oi}`}
                    className={`w-full text-left text-sm px-4 py-2.5 rounded-md border transition-colors flex items-center gap-2 ${cls}`}>
                    {submitted && isCorrect && <Check size={15} />}
                    {submitted && chosen && !isCorrect && <X size={15} />}
                    <span>{lang === "de" ? opt.de : opt.en}</span>
                  </button>
                );
              })}
            </div>
            {submitted && <p className="mt-2 text-xs text-wachs-dim italic">{lang === "de" ? q.de_exp : q.en_exp}</p>}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        {!submitted ? (
          <button onClick={submit} disabled={Object.keys(answers).length < courseQuiz.length} data-testid="quiz-submit"
            className="bg-sonne text-nacht font-medium px-6 py-3 rounded-md hover:opacity-90 transition-opacity disabled:opacity-40">
            {t.submit}
          </button>
        ) : (
          <button onClick={reset} data-testid="quiz-retry" className="inline-flex items-center gap-2 border border-bronze/40 text-bronze-soft px-5 py-3 rounded-md hover:bg-bronze/10 transition-colors">
            <RotateCcw size={15} /> {t.retry}
          </button>
        )}
      </div>

      {submitted && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="quiz-result"
          className={`mt-5 rounded-lg p-5 flex items-center gap-3 border ${passed ? "border-ready/50 bg-ready/10" : "border-sonne/50 bg-sonne/10"}`}>
          <Award className={passed ? "text-ready" : "text-sonne"} />
          <div>
            <p className="font-mono text-sm">{t.result}: {score} {t.of} {courseQuiz.length}</p>
            <p className={`text-sm ${passed ? "text-ready" : "text-sonne"}`}>{passed ? t.passed : t.failed}</p>
          </div>
        </motion.div>
      )}

      {submitted && passed && (
        <div className="mt-5 grid-panel rounded-lg p-5" data-testid="certificate-block">
          <div className="flex items-center gap-2 text-sonne"><Award size={18} /><span className="instrument-label">{t.certTitle}</span></div>
          <div className="mt-3 flex flex-col sm:flex-row gap-3 max-w-lg">
            <input value={certName} onChange={(e) => setCertName(e.target.value)} placeholder={t.certPlaceholder}
              data-testid="certificate-name" className="coi-input" />
            <a href={certName.trim() ? `${API}/course/certificate.pdf?name=${encodeURIComponent(certName.trim())}&lang=${lang}` : undefined}
              target="_blank" rel="noopener noreferrer" data-testid="certificate-download"
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap px-5 py-3 rounded-md font-medium transition-opacity ${certName.trim() ? "bg-sonne text-nacht hover:opacity-90" : "bg-sonne/30 text-nacht/50 pointer-events-none"}`}>
              <FileDown size={16} /> {t.certBtn}
            </a>
            <button onClick={sharepic} data-testid="certificate-sharepic"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-5 py-3 rounded-md font-medium border border-bronze/40 text-bronze-soft hover:bg-bronze/10 transition-colors">
              <FileDown size={16} /> {lang === "de" ? "Sharepic (Insta)" : "Sharepic (Insta)"}
            </button>
          </div>
        </div>
      )}

      {/* Reel series */}
      <div className="mt-12 flex items-center gap-2 text-sonne"><Clapperboard size={18} /><h2 className="font-serif text-2xl">{t.reels}</h2></div>
      <p className="mt-1 text-sm text-wachs-dim">{t.reelsSub}</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {awarenessReels.map((r) => {
          const c = lang === "de" ? r.de : r.en;
          return (
            <div key={r.id} data-testid={`reel-${r.id}`} className="grid-panel rounded-lg p-5">
              <p className="font-serif text-lg text-wachs">{c.hook}</p>
              <p className="mt-2 text-xs"><span className="instrument-label text-bronze-soft">{t.script}:</span> <span className="text-wachs-dim">{c.script}</span></p>
              <p className="mt-2 text-xs"><span className="instrument-label text-bronze-soft">{t.visual}:</span> <span className="text-wachs-dim">{c.visual}</span></p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
